import React, { useEffect, useState } from "react";
import Sidebar from "../../components/Sidebar/Sidebar";
import { Page } from "../../components/Layout/Page";
import { PageSection } from "../../components/Layout/PageSection";
import { get } from "../../config/Axios";
import { SectionHeader } from "../../components/SectionHeader/SectionHeader";
import { Alert, Button, Col, notification, Row, Spin } from "antd";
import Modal from "antd/lib/modal/Modal";
import Text from "antd/lib/typography/Text";
import { parse } from "json2csv";
import { Countries } from "../../util/countries";

type PaidSubscriptionReport = {
  status: string;
  plan: string;
  endPeriod: string;
  interval?: string;
};

type FreeSubscriptionReport = Pick<PaidSubscriptionReport, "plan">;

type CustomerReport = {
  email: string;
  givenName?: string;
  familyName?: string;
  phoneNumber?: string;
  alpha2?: string;
  tradePartner: string;
  subscription: PaidSubscriptionReport;
  address: string;
  postcode: string;
  cphNumber: string;
};
type CustomerReportMadeCSVFriendly = {
  "Email Address": string;
  "First Name"?: string;
  "Last Name"?: string;
  "Mobile phone Number"?: string;
  Package?: string;
  "Subscription end date"?: string;
  "Subscription Type"?: string;
  Country?: string;
  "Country alpha2"?: string;
  "Trade Partner": string;
  Address: string;
  Postcode: string;
  "CPH Number": string;
};

/**
 * Type guard to  tighten types on the subscription
 * @param subscription
 */
const isPaid = (subscription: PaidSubscriptionReport): boolean => {
  return subscription.plan !== "Free";
};

const prettyStatus = (status: string) => {
  const table: Record<string, string> = {
    past_due: "Past Due",
    active: "Active",
    trialing: "Trialing",
    canceled: "Canceled",
  };
  return table[status] || status;
};

const ReportsPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [data, setData] = useState<CustomerReportMadeCSVFriendly[] | null>(
    null
  );
  const [blob, setBlob] = useState<Blob | null>(null);

  const fetchUserListReport = async () => {
    if (loading) return;
    setModalOpen(true);
    setLoading(true);
    const d: CustomerReportMadeCSVFriendly[] = [];
    try {
      const result = await get<CustomerReport[]>({ path: "/reports/daily" });
      for (const r of result.data) {
        const {
          email,
          familyName,
          givenName,
          phoneNumber,
          subscription,
          alpha2,
          tradePartner,
          address,
          postcode,
          cphNumber,
        } = r;
        const country = Countries.find((c) => c.alpha2 === alpha2);
        const cr: CustomerReportMadeCSVFriendly = {
          "Email Address": email,
          "First Name": givenName,
          "Last Name": familyName,
          "Mobile phone Number": phoneNumber,
          Package: subscription.plan,
          "Subscription end date": subscription.endPeriod,
          "Subscription Type": subscription.interval,
          Country: country?.Country,
          "Country alpha2": country?.alpha2,
          "Trade Partner": tradePartner,
          Address: address,
          Postcode: postcode,
          "CPH Number": cphNumber,
        };

        // just a suffix on the package name for the status
        if (isPaid(subscription))
          cr.Package = `${cr.Package} - ${prettyStatus(subscription.status)}`;

        d.push(cr);
      }
    } catch (error) {
      notification.error({
        message: "It didn't work, tell Stephen", // TODO yes I know this is no good, but it's internal and I don't have time
        placement: "bottomRight",
      });
    }
    setData(d);
    setLoading(false);
  };

  const createBlob = (data: CustomerReportMadeCSVFriendly[]) => {
    const fields: (keyof CustomerReportMadeCSVFriendly)[] = [
      "Email Address",
      "First Name",
      "Last Name",
      "Mobile phone Number",
      "Package",
      "Subscription end date",
      "Subscription Type",
      "Country",
      "Country alpha2",
      "Trade Partner",
      "Address",
      "Postcode",
      "CPH Number",
    ];
    return new Blob([parse(data!, { fields })], {
      type: "text/plain",
    });
  };

  useEffect(() => {
    if (!data) return;
    setBlob(createBlob(data));
  }, [data]);

  return (
    <Sidebar
      content={() => {
        return (
          <Page title="Reports">
            <Modal
              visible={modalOpen}
              footer={null}
              onCancel={() => setModalOpen(false)}
            >
              <Row justify="center" gutter={[0, 32]}>
                <Col>
                  <Text>Generating report</Text>
                </Col>
                <Col>
                  <Alert
                    showIcon
                    type="info"
                    message="Long process"
                    description="This could take a long time to complete, please allow it to finish before closing your browser tab or leaving the page. That includes you, Ewen"
                  />
                </Col>
              </Row>
              <Row justify="center" gutter={[0, 32]}>
                <Col>
                  {loading ? (
                    <Spin />
                  ) : (
                    <Button
                      loading={loading}
                      href={blob ? URL.createObjectURL(blob) : undefined}
                      download="report.csv"
                    >
                      Download
                    </Button>
                  )}
                </Col>
              </Row>
            </Modal>
            <PageSection
              header={() => (
                <SectionHeader
                  text="List users"
                  subtext="Get a report of your active users and their data"
                />
              )}
            >
              <Row gutter={[0, 24]}>
                <Col>
                  <Button
                    disabled={loading}
                    onClick={() => fetchUserListReport()}
                  >
                    Run report
                  </Button>
                </Col>
              </Row>
              {!modalOpen && (loading || data) && (
                <Row gutter={[16, 16]} align="middle">
                  <Col>
                    <Text>
                      {data ? "Ready" : "Your file will be ready soon"}
                    </Text>
                  </Col>
                  <Col>
                    <Button
                      loading={loading}
                      href={blob ? URL.createObjectURL(blob) : undefined}
                      download="report.csv"
                    >
                      Download
                    </Button>
                  </Col>
                </Row>
              )}
            </PageSection>
          </Page>
        );
      }}
    />
  );
};

export default ReportsPage;
